import { Box, Chip, ChipProps, Skeleton, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { GridRenderCellParams } from "@mui/x-data-grid-pro";
import AcxButton from "components/UI/AcxButton/AcxButton";
import AcxDataGrid from "components/UI/AcxDataGrid/AcxDataGrid";
import AcxDataGridStore from "components/UI/AcxDataGrid/AcxDataGridStore";
import IColDef from "components/UI/AcxDataGrid/IColDef";
import AcxMainTextField from "components/UI/AcxMainTextField";
import * as AYSDialog from "components/UI/AYSDialog";
import { observer } from "mobx-react";
import { ClassifierStatus } from "models/ClientBrain/ClassifierStatus";
import React, { useEffect, useMemo, useState } from "react";
import { AuthStore } from "stores/AuthStore";
import ClientBrainStore from "stores/ClientBrainStore";
import ClientBrainSectionCard from "../ClientBrainSectionCard";
import { gridHeight } from "../clientBrainUtils";

type Props = {
    store: ClientBrainStore;
    authStore: AuthStore;
    canEdit?: boolean;
};

type ClassifierChange = {
    id: string;
    name: string;
    before: string;
    after: string;
};

function diffClassifiers(
    saved: ClassifierStatus[],
    draft: ClassifierStatus[],
): ClassifierChange[] {
    const savedById = new Map(saved.map((classifier) => [classifier.id, classifier]));
    const changes: ClassifierChange[] = [];

    draft.forEach((classifier) => {
        const savedClassifier = savedById.get(classifier.id);
        if (!savedClassifier) return;

        const before = (savedClassifier.description ?? "").trim();
        const after = (classifier.description ?? "").trim();

        if (before !== after) {
            changes.push({
                id: classifier.id,
                name: classifier.name,
                before: before || "None",
                after: after || "None",
            });
        }
    });

    return changes;
}

function statusChipColor(status: string): ChipProps["color"] {
    switch (status) {
        case "Published":
            return "success";
        case "Inactive":
            return "warning";
        case "Archived":
        default:
            return "default";
    }
}

const StatusChip: React.FC<{ status: string }> = ({ status }) => (
    <Chip size="small" label={status} color={statusChipColor(status)} />
);

const DescriptionCell: React.FC<{
    row: ClassifierStatus;
    editing: boolean;
    onDescriptionChange: (id: string, description: string) => void;
}> = ({ row, editing, onDescriptionChange }) => {
    const theme = useTheme();
    const description = row.description?.trim() ?? "";

    if (!editing) {
        return (
            <Tooltip title={description} disableHoverListener={!description}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        height: "100%",
                        width: "100%",
                    }}
                >
                    <Typography
                        noWrap
                        sx={{
                            color: description
                                ? theme.palette.gray[700]
                                : theme.palette.gray[400],
                            fontSize: "13px",
                            fontStyle: description ? "normal" : "italic",
                            width: "100%",
                        }}
                    >
                        {description || "No description added."}
                    </Typography>
                </Box>
            </Tooltip>
        );
    }

    return (
        <Tooltip title={description} disableHoverListener={!description}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    height: "100%",
                    width: "100%",
                }}
            >
                <AcxMainTextField
                    id={`client-brain-classifier-description-${row.id}`}
                    value={row.description ?? ""}
                    onChange={(e) => onDescriptionChange(row.id, e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    fullWidth
                    containerStyle={{ width: "100%" }}
                    textContainerSx={{ width: "100%" }}
                    placeholderText="Add a classifier description"
                />
            </Box>
        </Tooltip>
    );
};

const ClassifierGroupGrid: React.FC<{
    store: ClientBrainStore;
    groupName: string;
    classifiers: ClassifierStatus[];
    canEdit: boolean;
}> = observer(({ store, groupName, classifiers, canEdit }) => {
    const theme = useTheme();
    const [dgStore] = useState(
        () => new AcxDataGridStore(`client-brain-classifiers-${groupName}`),
    );
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [draftClassifiers, setDraftClassifiers] = useState<ClassifierStatus[]>([]);
    const [showConfirm, setShowConfirm] = useState(false);

    const rows = editing ? draftClassifiers : classifiers;
    const pendingChanges = useMemo(
        () => diffClassifiers(classifiers, draftClassifiers),
        [classifiers, draftClassifiers],
    );
    const hasChanges = pendingChanges.length > 0;

    const startEdit = () => {
        setDraftClassifiers(classifiers.map((classifier) => ({ ...classifier })));
        setEditing(true);
    };

    const cancelEdit = () => {
        setDraftClassifiers([]);
        setEditing(false);
        setShowConfirm(false);
    };

    const updateDescription = (id: string, description: string) => {
        setDraftClassifiers((current) =>
            current.map((classifier) =>
                classifier.id === id ? { ...classifier, description } : classifier,
            ),
        );
    };

    const confirmSave = async () => {
        const updates = pendingChanges.map((change) => ({
            id: change.id,
            description: change.after === "None" ? "" : change.after,
        }));

        setSaving(true);
        try {
            await store.updateClassifierDescriptions(updates);
            setEditing(false);
            setDraftClassifiers([]);
            setShowConfirm(false);
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        const columns: IColDef[] = [
            { headerName: "Name", field: "name", width: 240 },
            {
                headerName: "Description",
                field: "description",
                flex: 1,
                sortable: false,
                filterable: false,
                renderCell: (params: GridRenderCellParams<ClassifierStatus>) => (
                    <DescriptionCell
                        row={params.row}
                        editing={editing}
                        onDescriptionChange={updateDescription}
                    />
                ),
            },
            {
                headerName: "Status",
                field: "status",
                width: 140,
                renderCell: (params: GridRenderCellParams) => (
                    <StatusChip status={params.value} />
                ),
            },
        ];
        dgStore.checkboxSelection = false;
        dgStore.disableLocalConfig = true;
        dgStore.pagination = false;
        dgStore.hideFooterPagination = true;
        dgStore.hideHeader = true;
        dgStore.disableRowSelectionOnClick = true;
        dgStore.setColumns(columns);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dgStore, editing]);

    useEffect(() => {
        dgStore.setRows(rows);
        dgStore.isLoading = saving;
        dgStore.heightOverride = gridHeight(rows.length);
    }, [dgStore, rows, saving]);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <Typography
                    sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: theme.palette.gray[700],
                    }}
                >
                    {groupName}
                </Typography>
                {!editing && canEdit && (
                    <AcxButton color="primary" variant="text" onClick={startEdit}>
                        Edit
                    </AcxButton>
                )}
            </Box>
            <AcxDataGrid dataGridStore={dgStore} />
            {editing && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1,
                    }}
                >
                    <AcxButton color="white" onClick={cancelEdit} disabled={saving}>
                        Cancel
                    </AcxButton>
                    <AcxButton
                        color="primary"
                        onClick={() => setShowConfirm(true)}
                        disabled={!hasChanges}
                        loading={saving}
                    >
                        Save
                    </AcxButton>
                </Box>
            )}
            <AYSDialog.Container
                isOpen={showConfirm}
                onClose={() => setShowConfirm(false)}
                id={`confirm-client-brain-classifier-changes-${groupName}`}
                maxWidth="md"
            >
                <AYSDialog.Title>
                    Confirm changes to {groupName} classifiers
                </AYSDialog.Title>
                <AYSDialog.Subtitle>
                    Review the changes below before saving.
                </AYSDialog.Subtitle>
                <AYSDialog.Content>
                    <Stack direction="column" rowGap={1.5}>
                        {pendingChanges.map((change) => (
                            <Stack key={change.id} direction="column" rowGap={0.25}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {change.name}
                                </Typography>
                                <Typography variant="body2">
                                    {change.before} {" -> "} {change.after}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                </AYSDialog.Content>
                <AYSDialog.Actions>
                    <AcxButton
                        color="white"
                        onClick={() => setShowConfirm(false)}
                        disabled={saving}
                    >
                        Cancel
                    </AcxButton>
                    <AcxButton color="primary" onClick={confirmSave} loading={saving}>
                        Save Changes
                    </AcxButton>
                </AYSDialog.Actions>
            </AYSDialog.Container>
        </Box>
    );
});

const ActiveClassifiersSection: React.FC<Props> = observer(({
    store,
    authStore,
    canEdit,
}) => {
    const canEditClassifiers =
        canEdit ?? (authStore.isUserUltra() || authStore.canUserEdit("KBo"));
    const theme = useTheme();
    const groups = Object.entries(store.classifierGroups);

    return (
        <ClientBrainSectionCard
            title="Active Classifiers"
            subtitle="Authenticx conversation classifiers running for this client. Managed by Authenticx — these determine what KBo can analyze."
        >
            {store.isLoading ? (
                <Skeleton variant="rounded" height={200} />
            ) : groups.length === 0 ? (
                <Typography
                    sx={{
                        fontSize: "14px",
                        color: theme.palette.gray[400],
                        fontStyle: "italic",
                    }}
                >
                    No classifiers found for this client.
                </Typography>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    {groups.map(([groupName, classifiers]) => (
                        <ClassifierGroupGrid
                            key={groupName}
                            store={store}
                            groupName={groupName}
                            classifiers={classifiers}
                            canEdit={canEditClassifiers}
                        />
                    ))}
                </Box>
            )}
        </ClientBrainSectionCard>
    );
});

export default ActiveClassifiersSection;
